<template>
  <article class="analytics-card platform-card">
    <div class="card-heading">
      <h3 class="card-label">Active listings by platform</h3>
      <button type="button" class="view-trends" aria-label="View platform trends" title="View platform trends" aria-haspopup="dialog" @click="$emit('view-trends')"><i class="bi-graph-up" aria-hidden="true" /></button>
    </div>
    <template v-if="sources.length">
      <ul class="platform-chart" aria-label="Active listings and verification by platform">
        <li v-for="row in sources" :key="row.source">
          <div class="platform-heading">
            <span class="platform-symbol"><img v-if="row.iconUrl && !failedIcons[row.source]" :src="row.iconUrl" alt="" width="20" height="20" @error="failedIcons[row.source] = true" /><span>{{ row.label }}</span></span>
            <strong>{{ fmtInt(row.value) }}</strong>
          </div>
          <div class="platform-track" role="img" :aria-label="row.verification ? `${row.label}: ${row.verification.verified} verified, ${row.verification.unverified} unverified, ${row.verification.deferred} deferred listings` : `${row.label}: ${row.value} active listings. Verification breakdown unavailable.`">
            <div class="platform-bar" :style="{ width: `${100 * row.value / largestCount}%` }">
              <span v-for="segment in row.segments" :key="segment.key" :style="{ flexGrow: segment.value, background: segment.color }" />
            </div>
          </div>
          <div v-if="row.verification" class="verification-counts">
            <span v-for="segment in row.segments" :key="segment.key"><span class="legend-dot" :style="{ background: segment.color }" />{{ fmtInt(segment.value) }} {{ segment.label.toLowerCase() }}</span>
          </div>
          <p v-else class="fine-print">Verification breakdown unavailable.</p>
        </li>
      </ul>
      <p v-if="sources.some(row => row.verification)" class="fine-print verification-note">Verified listings have a confirmed property / address. Deferred listings are shown separately and excluded from verified / unverified counts.</p>
    </template>
    <p v-else class="fine-print">{{ total === 0 ? 'No active listings.' : 'Platform counts are not available in this snapshot.' }}</p>
  </article>
</template>

<script setup>
/* global defineProps, defineEmits */
import { computed, ref } from 'vue';
import { getListingSourceInfo } from '@strenforcement/domain/listing';
import { fmtInt } from '../../../../dashboard/public/js/format.js';
const props = defineProps({ rows: { type: Array, default: () => [] }, total: Number });
defineEmits(['view-trends']);
const failedIcons = ref({});
const statusStyles = [
  { key: 'verified', label: 'Verified', color: '#3b7dd8' },
  { key: 'unverified', label: 'Unverified', color: '#d4870b' },
  { key: 'deferred', label: 'Deferred', color: '#9e9e9e' }
];
const sources = computed(() => (Array.isArray(props.rows) ? props.rows : []).filter(row => Number.isSafeInteger(row.count) && row.count > 0).map(row => {
  const source = String(row.source || 'unknown').toLowerCase();
  const info = getListingSourceInfo(source === 'booking.com' ? 'booking' : source);
  const valid = statusStyles.every(({ key }) => Number.isSafeInteger(row.verification?.[key]) && row.verification[key] >= 0)
    && statusStyles.reduce((sum, { key }) => sum + row.verification[key], 0) === row.count;
  return { source: row.source, label: info.name, iconUrl: info.iconUrl, value: row.count,
    verification: valid ? row.verification : null,
    segments: valid ? statusStyles.map(style => ({ ...style, value: row.verification[style.key] })).filter(segment => segment.value > 0)
      : [{ key: 'total', value: row.count, color: '#3b7dd8' }]
  };
}));
const largestCount = computed(() => Math.max(1, ...sources.value.map(row => row.value)));
</script>

<style scoped>
.card-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 18px; }
.card-label { font-size: 13px; font-weight: 500; color: var(--text-secondary); margin: 0; line-height: 1.5; }
.view-trends { display: inline-flex; align-items: center; justify-content: center; border: 1px solid var(--border); border-radius: 6px; width: 30px; height: 30px; padding: 0; background: transparent; color: var(--text-secondary); font-size: 13px; }
.view-trends:hover { border-color: var(--border-hover); color: var(--text-primary); }
.view-trends:focus-visible { outline: 2px solid var(--blue); outline-offset: 2px; }
@media (pointer: coarse) { .view-trends { width: 44px; height: 44px; } }
.platform-chart { list-style: none; margin: 0; padding: 0; display: grid; gap: 20px; }
.platform-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 7px; font-size: 12px; }
.platform-heading strong { font-weight: 500; font-family: var(--bs-font-monospace); }
.platform-symbol { display: flex; align-items: center; gap: 6px; min-width: 0; overflow-wrap: anywhere; }
.platform-symbol img { object-fit: contain; border-radius: 4px; }
.platform-track { width: 100%; }
.platform-bar { display: flex; gap: 2px; height: 14px; border-radius: 3px; overflow: hidden; }
.platform-bar > span { flex-basis: 0; min-width: 0; }
.verification-counts { display: flex; flex-wrap: wrap; gap: 4px 10px; margin-top: 7px; font-size: 11px; color: var(--text-secondary); }
.verification-counts > span { display: inline-flex; align-items: center; gap: 4px; }
.legend-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; flex: 0 0 7px; }
.fine-print { font-size: 12px; line-height: 1.5; color: var(--text-secondary); margin: 6px 0 0; }
.verification-note { margin-top: 18px; }
</style>
